import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

/**
 * Phase 2 Week 6.2: Auth Event Listener
 * 
 * Invalidates React Query cache on auth state changes to prevent stale data.
 * 
 * Features:
 * - SIGNED_IN / TOKEN_REFRESHED → invalidate stakeholder queries
 * - SIGNED_OUT → clear all cache
 * - Prevents cached empty data from anon fetches
 */
export const AuthEventListener = () => {
  const queryClient = useQueryClient();
  const lastUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      logger.debug('[AuthEventListener] Auth event:', { 
        event, 
        hasSession: !!session?.access_token 
      });

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {

        const userId = session?.user?.id ?? null;

        // Chỉ clear cache khi USER ĐỔI THẬT (login lần đầu / đổi user).
        // KHÔNG clear khi chỉ token-refresh / refocus cùng user — vì xoá cache role/permission
        // → isLoading=true → ProtectedRoute remount → mất state đang nhập (dialog).
        if (userId && userId !== lastUserIdRef.current) {

          queryClient.removeQueries({
            predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'stakeholders',
          });
          queryClient.invalidateQueries({
            predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'stakeholders',
          });

          queryClient.removeQueries({ queryKey: ['user-roles', userId] });
          queryClient.removeQueries({ queryKey: ['admin-fallback-check', userId] });

          queryClient.removeQueries({
            predicate: (q) =>
              Array.isArray(q.queryKey) &&
              q.queryKey[0] === 'check-permission' &&
              typeof q.queryKey[1] === 'string' &&
              q.queryKey[1].startsWith('ROADMAP_')
          });

          logger.debug('[AuthEventListener] Cleared cache on user change', event);
        }

        lastUserIdRef.current = userId;
      }

      if (event === 'SIGNED_OUT') {
        // Clear all cache on logout
        queryClient.clear();
        lastUserIdRef.current = null;
        logger.debug('[AuthEventListener] Cleared all cache');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [queryClient]);

  return null;
};
